import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from './api'

const BROWSE_LIMIT = 50
const SEARCH_LIMIT = 100

/**
 * Backs a searchable list page: `query` empty browses (cursor-paginated, "load more"); non-empty
 * switches to a server-side search (a capped list). A request-generation guard means a slow, stale
 * response can never overwrite a newer one's results.
 */
export function useCatalogue<T>(path: string, query: string, noun: string, params: Record<string, string> = {}) {
  const [items, setItems] = useState<T[]>([])
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const requestId = useRef(0)
  const extra = Object.entries(params)
    .filter(([, v]) => v)
    .map(([k, v]) => `&${k}=${encodeURIComponent(v)}`)
    .join('')

  const load = useCallback(
    async (q: string) => {
      const id = ++requestId.current
      setLoading(true)
      setError(null)
      try {
        if (q.trim()) {
          const results = await api.get<T[]>(`${path}?q=${encodeURIComponent(q.trim())}&limit=${SEARCH_LIMIT}${extra}`)
          if (id !== requestId.current) return
          setItems(results)
          setNextCursor(null)
        } else {
          const page = await api.getPage<T>(`${path}?limit=${BROWSE_LIMIT}${extra}`)
          if (id !== requestId.current) return
          setItems(page.data)
          setNextCursor(page.nextCursor)
        }
      } catch (err) {
        if (id !== requestId.current) return
        setError(err instanceof Error ? err.message : `Could not load ${noun}.`)
      } finally {
        if (id === requestId.current) setLoading(false)
      }
    },
    [path, noun, extra]
  )

  useEffect(() => {
    void load(query)
  }, [query, load])

  async function loadMore() {
    if (!nextCursor || loadingMore) return
    setLoadingMore(true)
    try {
      const page = await api.getPage<T>(`${path}?cursor=${encodeURIComponent(nextCursor)}&limit=${BROWSE_LIMIT}${extra}`)
      setItems((prev) => [...prev, ...page.data])
      setNextCursor(page.nextCursor)
    } catch (err) {
      setError(err instanceof Error ? err.message : `Could not load more ${noun}.`)
    } finally {
      setLoadingMore(false)
    }
  }

  return { items, loading, loadingMore, error, hasMore: nextCursor !== null, loadMore }
}

export function useDebounced(value: string, ms = 300) {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms)
    return () => clearTimeout(timer)
  }, [value, ms])
  return debounced
}
